<template>
    <div>
        <loader v-if="isLoading" />
        <b-table-simple
            v-else
            borderless
            hover
            class="list"
            :sort-by.sync="sortBy"
            :sort-desc.sync="sortDesc"
        >
            <colgroup>
                <col style="width: 9%;" />
                <col style="width: 23%;" />
                <col style="width: 5%;" />
                <col style="width: 10%;" />
                <col style="width: 10%;" />
                <col style="width: 31%;" />
                <col style="width: 8%;" />
                <col style="width: 4%;" />
            </colgroup>
            <b-thead>
                <b-tr>
                    <b-th>Return:</b-th>
                    <b-th>Client:</b-th>
                    <b-th>Items:</b-th>
                    <b-th>Total:</b-th>
                    <b-th>Issued:</b-th>
                    <b-th>Note:</b-th>
                    <b-th>Date:</b-th>
                    <b-th></b-th>
                </b-tr>
            </b-thead>
            <b-tbody>
                <b-tr v-for="(r, i) in returns" :key="i">
                    <b-td class="text-nowrap">
                        <return-link v-if="r.encoded_id" :return="r" />
                    </b-td>
                    <b-td>
                        <client-name v-if="r.client" :client="r.client" />
                    </b-td>
                    <b-td> {{ r.item_count }} </b-td>

                    <b-td>
                        {{ _toCurrency(r.return_amount) }}
                    </b-td>
                    <b-td class="text-nowrap">
                        <span
                            v-if="r.active_credit"
                            @click="
                                showEditCredit(
                                    r,
                                    r.active_credit,
                                    _hasAccess('billing.credit.update')
                                )
                            "
                            :class="{
                                'text-link': _hasAccess('billing.credit.update')
                            }"
                        >
                            Credit #{{ r.active_credit.index }}
                        </span>
                        <span v-if="!_.isEmpty(r.active_refunds)">
                            <div
                                v-for="refund in r.active_refunds"
                                :key="refund.id"
                            >
                                <span
                                    @click="
                                        showEditRefund(
                                            r,
                                            refund,
                                            _hasAccess('billing.refund.update')
                                        )
                                    "
                                    :class="{
                                        'text-link': _hasAccess(
                                            'billing.refund.update'
                                        )
                                    }"
                                >
                                    Refund #{{ refund.index }}
                                </span>
                            </div>
                        </span>
                    </b-td>
                    <b-td>
                        {{ r.note }}
                    </b-td>
                    <b-td class="text-nowrap">
                        {{ _carbonDateDisplay(r.return_date) }}
                    </b-td>
                    <b-td v-if="loadingTransactionId != r.id">
                        <b-dropdown
                            v-if="isHasActions(r)"
                            variant="link"
                            toggle-class="text-decoration-none"
                            no-caret
                            right
                        >
                            <template v-slot:button-content>
                                <i class="cus-cog-down"></i>
                            </template>

                            <b-dropdown-item
                                v-if="isCanEdit(r)"
                                :href="
                                    env.APPS +
                                        'billing/returns/view/' +
                                        r.encoded_id || '#'
                                "
                                target="_blank"
                            >
                                <i class="cus-page-white-edit"></i>
                                Edit Return
                            </b-dropdown-item>

                            <b-dropdown-item
                                v-if="isCanPost(r)"
                                href="#"
                                @click="openReturnPost(r)"
                            >
                                <i class="cus-accept"></i>
                                Post Return
                            </b-dropdown-item>

                            <b-dropdown-item
                                v-if="isCanEmail(r)"
                                href="#"
                                @click="openReturnEmailModal(r)"
                            >
                                <i class="cus-email"></i>
                                Email Return
                            </b-dropdown-item>

                            <b-dropdown-item
                                v-if="isCanPrint(r)"
                                href="#"
                                @click="generatePrintPdf(r)"
                            >
                                <i class="cus-printer"></i>
                                Print Return
                            </b-dropdown-item>

                            <b-dropdown-item
                                v-if="isCanDelete(r)"
                                href="#"
                                @click="openDeleteReturnModal(r)"
                            >
                                <i class="cus-delete"></i>
                                Delete Return
                            </b-dropdown-item>
                        </b-dropdown>
                    </b-td>
                    <b-td class="text-right" v-else>
                        <moon-loader color="black" size="16px" />
                    </b-td>
                </b-tr>
                <b-tr v-if="returns.length === 0">
                    <b-td colspan="8">
                        <i>No records available</i>
                    </b-td>
                </b-tr>
            </b-tbody>
        </b-table-simple>

        <b-pagination
            v-if="meta && meta.lastPage > 1"
            class="vet-pagination"
            align="right"
            v-model="meta.currentPage"
            :total-rows="meta.total"
            :per-page="meta.perPage"
            prev-text="Prev"
            next-text="Next"
            :hide-goto-end-buttons="true"
            @change="getReturns"
        />

        <return-delete-modal
            modal-id="return-delete-modal"
            :return-details="selectedReturn"
            @reload="getReturns(meta.currentPage)"
        />
        <return-email-modal
            modal-ref="return-email-modal"
            ref="return-email-modal"
            :return-details="selectedReturn"
            :client="selectedReturn.client"
        />
        <credit-edit-modal
            ref="creditEditModal"
            modalId="creditEditModal"
            ok-title="Save"
            modal-ref="creditEditModal"
            @reload="redirectOtherTab('tab-credits')"
        />
        <post-modal
            ref="postModal"
            modal-ref="postModal"
            @reload="getReturns(meta.currentPage)"
        />
        <refund-edit-modal
            modal-ref="refundEditModal"
            ref="refundEditModal"
            :refund="selectedRefund"
            :client-name="selectedClientname"
            @reload="redirectOtherTab('tab-refunds')"
        />
    </div>
</template>

<script>
/**
 * @name ReturnList
 * @description List of return
 * @see /apps/application/views/staff/billing/sub/return/list.php
 */
import Loader from "@/components/elements/loader-list";
import BillingReturnServices from "@/services/billing/billing-return";
import EXTENSIONS from "@/constants/extensions";
import MoonLoader from "vue-spinner/src/MoonLoader.vue";

export default {
    mixins: {},
    components: {
        Loader,
        MoonLoader,
        ClientName: () => import("@/components/commons/client-name"),
        ReturnLink: () => import("@/components/commons/return-link"),
        ReturnDeleteModal: () =>
            import("@/components/client-returns/return-delete-modal"),
        ReturnEmailModal: () =>
            import("@/components/client-returns/return-email-modal"),
        RefundEditModal: () =>
            import("@/components/client-refunds/refund-edit"),
        CreditEditModal: () =>
            import("@/components/billing-credit/credit-edit-modal"),
        PostModal: () => import("@/components/billing-returns/post-modal")
    },
    props: {
        status: {
            type: Number,
            default: 2
        }
    },

    data() {
        return {
            sortBy: "Return:",
            sortDesc: true,
            isLoading: false,
            returns: [],
            selectedReturn: {},
            selectedRefund: {},
            selectedClientname: null,
            loadingTransactionId: "",
            meta: null
        };
    },
    async mounted() {
        this.getReturns();
    },
    methods: {
        async openReturnPost(returned) {
            let valid = true;
            returned.name = "Return #" + returned.index;
            this.loadingTransactionId = returned.id;
            if (this._isExtensionEnabled(EXTENSIONS.CODE.CODE_STRIPE)) {
                await BillingReturnServices.postReturnStripeInteract({
                    return_id: returned.id
                }).then(result => {
                    if (result.message.code == 200) {
                        if (result.message.type == "interac_present") {
                            returned.isInteracPayment = true;
                            returned.isInteracPaymentData = {
                                trans_type: result.message.type,
                                charge: result.message.charge,
                                payment_intent: result.message.payment_intent
                            };
                        }
                    } else if (result.message.code == 401) {
                        valid = false;
                        this.loadingTransactionId = "";
                        this._message(
                            result.message.message,
                            "danger",
                            5000,
                            "50%"
                        );
                    } else {
                        valid = false;
                        this.loadingTransactionId = "";
                        this._message(result.message.message, "danger");
                    }
                });
            }
            if (valid) {
                this.$refs["postModal"].open(returned);
                this.loadingTransactionId = "";
            }
        },
        generatePrintPdf(returned) {
            BillingReturnServices.generatePrintPdf(returned.id)
                .then(response => {
                    if (
                        response.code === 200 &&
                        response.resource &&
                        response.resource.path
                    ) {
                        this._redirect(response.resource.path, true);
                    } else {
                        throw "error";
                    }
                })
                .catch(error => {
                    this._message("Error : Unable print the return", "danger");
                    console.error(error);
                });
        },
        openDeleteReturnModal(returned) {
            this.selectedReturn = returned;
            this.selectedReturn.name = "Return #" + this.selectedReturn.index;
            this.$bvModal.show("return-delete-modal");
        },
        openReturnEmailModal(returned) {
            this.selectedReturn = returned;
            this.selectedReturn.name = "Return #" + this.selectedReturn.index;
            this.$refs["return-email-modal"].open();
        },
        showEditCredit(returned, credit, canEdit) {
            if (canEdit) {
                credit.client_id = returned.client.id;
                credit.clientLabel = returned.client.label;
                this.$refs.creditEditModal.open(credit);
            }
        },
        showEditRefund(returned, refund, canEdit) {
            if (canEdit) {
                this.selectedClientname = returned.client.label;
                this.selectedRefund = refund;
                this.selectedRefund.client = returned.client;
                this.selectedRefund.isHasReeturn = true;
                this.$refs.refundEditModal.open();
            }
        },
        getReturns(page = 1) {
            let self = this;
            self.returns = [];
            self.isLoading = true;
            const params = {
                page: page,
                perPage: self.PER_PAGE,
                status: self.status
            };
            BillingReturnServices.getReturns(params)
                .then(res => {
                    if (res) {
                        self.returns = res.resources;
                        self.meta = res.meta;
                    } else {
                        throw "Error : Unable to retrieve returns list";
                    }
                })
                .catch(error => {
                    this._message(
                        "Error : Unable to retrieve returns list",
                        "danger"
                    );
                    console.error(error);
                })
                .finally(() => {
                    self.isLoading = false;
                });
        },
        redirectOtherTab(tab) {
            this.$emit("redirect-other-tab", tab);
        },
        isBusinessActorActive(client) {
            return (
                client &&
                client.single_business_actor &&
                client.single_business_actor.isactive
            );
        },
        isCanEdit(r) {
            return (
                this.isBusinessActorActive(r.client) &&
                this._hasAccess("billing.return.update")
            );
        },
        isCanPost(r) {
            return (
                this._hasAccess("billing.return.post") &&
                r.item_count > 0 &&
                !r.status &&
                this.isBusinessActorActive(r.client) &&
                (this._hasAccess("billing.credit.create") ||
                    this._hasAccess("billing.refund.create"))
            );
        },
        isCanDelete(r) {
            return (
                this._hasAccess("billing.return.delete") &&
                this.isBusinessActorActive(r.client) &&
                !r.isCreditCardRefunds
            );
        },
        isCanEmail(r) {
            return r.status && this._hasAccess("billing.return.email");
        },
        isCanPrint(r) {
            return r.status && this._hasAccess("billing.return.print");
        },
        isHasActions(r) {
            return (
                this.isCanEdit(r) ||
                this.isCanPost(r) ||
                this.isCanEmail(r) ||
                this.isCanPrint(r) ||
                this.isCanDelete(r)
            );
        }
    }
};
</script>
